/**
 * Normalizes Salesforce / program status strings for comparisons (case, spacing, separators).
 */
export function normalizeApplicationStatus(status: string): string {
  return (status || '')
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ');
}

export function isPendingInformationStatus(normalized: string): boolean {
  return normalized === 'pending information';
}

/** Document rejected in SF — re-upload flow (distinct from Denied, which is view-only). */
export function isSalesforceRejectedStatus(normalized: string): boolean {
  return normalized === 'rejected';
}

export function isSalesforceDeniedStatus(normalized: string): boolean {
  return normalized === 'denied';
}

/** True when SF indicates resubmission after rejection or denial (legacy re_upload program status). */
export function isSalesforceRejectedOrDenied(normalized: string): boolean {
  return isSalesforceRejectedStatus(normalized) || isSalesforceDeniedStatus(normalized);
}

const VIEW_APPLICATION_TITLE: Record<string, string> = {
  approved: 'Approved',
  /** Salesforce sometimes returns singular "Approve" on engagement rows */
  approve: 'Approved',
  denied: 'Denied',
  'in review': 'In review',
  'not reviewed': 'Not Reviewed',
};

/**
 * Returns a primary button label like "Denied - View Application" for known Salesforce statuses
 * that should open read-only application view. Pending Information uses re-upload instead (returns null).
 */
export function getSalesforceViewApplicationLabel(raw: string | undefined | null): string | null {
  const n = normalizeApplicationStatus(typeof raw === 'string' ? raw : '');
  if (!n || isPendingInformationStatus(n)) {
    return null;
  }
  const title = VIEW_APPLICATION_TITLE[n];
  if (!title) {
    return null;
  }
  return `${title} - View Application`;
}

/**
 * Tries Salesforce field first, then program status — APIs may send picklist values in either field.
 */
export function getViewApplicationLabelFromEither(
  salesforceRaw: string | null | undefined,
  programRaw: string | null | undefined
): string | null {
  return (
    getSalesforceViewApplicationLabel(salesforceRaw ?? undefined) ||
    getSalesforceViewApplicationLabel(programRaw ?? undefined)
  );
}

export function isViewOnlyWorkflowOnEither(sfNorm: string, programNorm: string): boolean {
  return (
    isSalesforceViewOnlyWorkflowStatus(sfNorm) ||
    isSalesforceViewOnlyWorkflowStatus(programNorm)
  );
}

export function isPendingInformationOnEither(sfNorm: string, programNorm: string): boolean {
  return isPendingInformationStatus(sfNorm) || isPendingInformationStatus(programNorm);
}

/** Legacy portal statuses that mean "submitted / in flight" before Salesforce picklist alignment. */
export function isLegacySubmittedLikeStatus(normalized: string): boolean {
  const submittedStatuses = ['pending', 'pending approval', 'pending aproval'];
  return submittedStatuses.includes(normalized);
}

export function isSalesforceViewOnlyWorkflowStatus(normalized: string): boolean {
  return (
    normalized === 'approved' ||
    normalized === 'approve' ||
    normalized === 'denied' ||
    normalized === 'in review' ||
    normalized === 'not reviewed'
  );
}

function normalizedRowStatus(u: Record<string, unknown>): string {
  const raw = u?.['status'] ?? u?.['Status'];
  return normalizeApplicationStatus(typeof raw === 'string' ? raw : String(raw ?? ''));
}

/**
 * Row items from stored `user_program.updates` — supports legacy array or
 * `{ comment, items }` wrapper from Salesforce webhooks.
 */
export function getSalesforceUpdateItems(updates: unknown): Record<string, unknown>[] {
  if (Array.isArray(updates)) {
    return updates;
  }
  if (updates && typeof updates === 'object') {
    const items = (updates as Record<string, unknown>)['items'];
    return Array.isArray(items) ? items : [];
  }
  return [];
}

/** Application-level comment only from program-salesforce-detail (`comment` field). */
export function getProgramSalesforceMainComment(detail: unknown): string | null {
  if (!detail || typeof detail !== 'object') {
    return null;
  }
  const d = detail as Record<string, unknown>;
  const mainRaw = d['comment'] ?? d['Comment'];
  const main = typeof mainRaw === 'string' ? mainRaw.trim() : '';
  return main || null;
}

/** Application-level comment from stored updates wrapper (null for legacy array rows). */
export function getSalesforceStoredComment(updates: unknown): string | null {
  if (!updates || typeof updates !== 'object' || Array.isArray(updates)) {
    return null;
  }
  const raw =
    (updates as Record<string, unknown>)['comment'] ??
    (updates as Record<string, unknown>)['Comment'] ??
    (updates as Record<string, unknown>)['applicationComment'] ??
    (updates as Record<string, unknown>)['ApplicationComment'];
  return typeof raw === 'string' && raw.trim() ? raw.trim() : null;
}

/**
 * True if `updates` contains any row with Status "Pending Information".
 */
export function hasPendingInformationInSalesforceUpdates(updates: unknown): boolean {
  return getSalesforceUpdateItems(updates).some(
    (u: Record<string, unknown>) => normalizedRowStatus(u) === 'pending information'
  );
}

/**
 * True if `updates` contains any row with Status "Rejected" or "Denied" (document / checklist rejection).
 */
export function hasRejectedOrDeniedInSalesforceUpdates(updates: unknown): boolean {
  return getSalesforceUpdateItems(updates).some((u: Record<string, unknown>) => {
    const s = normalizedRowStatus(u);
    return s === 'rejected' || s === 'denied';
  });
}

/**
 * Re-upload when any row needs new uploads: Pending Information, Rejected, or Denied.
 */
export function hasSalesforceUpdatesRequiringReupload(updates: unknown): boolean {
  return (
    hasPendingInformationInSalesforceUpdates(updates) ||
    hasRejectedOrDeniedInSalesforceUpdates(updates)
  );
}
